import {
  AlertCircle,
  CheckSquare,
  CircleCheck,
  Clock,
  ListTodo,
  Square,
} from "lucide-react";
import type {
  CheckTasksCompletedResult,
  CreateTasksResult,
  FpMessagePart,
  GetNextTaskResult,
  GetTaskStatsResult,
  GetTasksToolResult,
  UpdateTaskResult,
} from "../../../../../schemas/codegen";
import {
  ExpandableMessageCard,
  SimpleMessageCard,
  isBootsmanResult,
} from "./shared";

interface TaskManagementProps {
  part: Extract<FpMessagePart, { type: "tool-call" }>;
}

// Task-related result types from central schemas
type TaskManagementResult =
  | CreateTasksResult
  | GetTasksToolResult
  | UpdateTaskResult
  | GetNextTaskResult
  | CheckTasksCompletedResult
  | GetTaskStatsResult;

function getTaskIcon(toolName: string) {
  switch (toolName) {
    case "createTasks":
      return ListTodo;
    case "updateTask":
      return CheckSquare;
    case "getTasks":
    case "getTaskStats":
      return ListTodo;
    case "getNextTask":
      return Clock;
    case "checkTasksCompleted":
      return CheckSquare;
    default:
      return Square;
  }
}

function getTaskResult(
  part: Extract<FpMessagePart, { type: "tool-call" }>,
): TaskManagementResult | null {
  if (
    part.status === "complete" &&
    part.result &&
    isBootsmanResult(part.result) &&
    part.result.success
  ) {
    return part.result.data as TaskManagementResult;
  }
  return null;
}

function getTaskTitle(part: Extract<FpMessagePart, { type: "tool-call" }>) {
  const { toolName, args } = part;
  const result = getTaskResult(part);

  switch (toolName) {
    case "createTasks": {
      const taskCount = Array.isArray(args?.tasks) ? args.tasks.length : 0;
      return `Creating ${taskCount} task${taskCount !== 1 ? "s" : ""}`;
    }
    case "updateTask": {
      const status = args?.status;

      // Get task title from result (if completed) or args (if provided)
      const updateResult = result as UpdateTaskResult | null;
      const taskTitleFromResult = updateResult?.taskTitle;
      const taskTitleFromArgs = args?.title;
      const taskTitle = taskTitleFromResult || taskTitleFromArgs;

      const taskDisplay = taskTitle ? `"${taskTitle}"` : "task";

      if (status) {
        const statusMap = {
          pending: "Pending",
          in_progress: "In Progress",
          completed: "Completed",
          cancelled: "Cancelled",
        };
        return `Marking ${taskDisplay} → ${statusMap[status as keyof typeof statusMap] || status}`;
      }
      return `Updating ${taskDisplay}`;
    }
    case "getTasks": {
      const getTasksResult = result as GetTasksToolResult | null;
      if (getTasksResult?.tasks) {
        const count = Array.isArray(getTasksResult.tasks)
          ? getTasksResult.tasks.length
          : 0;
        return `Retrieved ${count} task${count !== 1 ? "s" : ""}`;
      }
      return "Getting tasks";
    }
    case "getNextTask": {
      const getNextTaskResult = result as GetNextTaskResult | null;
      if (getNextTaskResult?.hasNextTask && getNextTaskResult.task?.title) {
        return `Next: "${getNextTaskResult.task.title}"`;
      }
      if (getNextTaskResult?.hasNextTask === false) {
        return "No pending tasks";
      }
      return "Getting next task";
    }
    case "checkTasksCompleted": {
      const checkTasksResult = result as CheckTasksCompletedResult | null;
      if (checkTasksResult?.allTasksCompleted !== undefined) {
        return checkTasksResult.allTasksCompleted
          ? "All tasks completed"
          : "Tasks remaining";
      }
      return "Checking task completion";
    }
    case "getTaskStats": {
      const getTaskStatsResult = result as GetTaskStatsResult | null;
      if (getTaskStatsResult) {
        const { total = 0, completed = 0 } = getTaskStatsResult;
        return `Task Overview: ${completed}/${total} completed`;
      }
      return "Getting task statistics";
    }
    default:
      return `Task: ${toolName}`;
  }
}

function shouldUseExpandableCard(
  part: Extract<FpMessagePart, { type: "tool-call" }>,
) {
  const { toolName } = part;
  const result = getTaskResult(part);

  // Use expandable for createTasks when we have results
  if (toolName === "createTasks") {
    const createTasksResult = result as CreateTasksResult | null;
    return (createTasksResult?.tasks?.length ?? 0) > 0;
  }

  // Use expandable for getTasks when we have multiple tasks
  if (toolName === "getTasks") {
    const getTasksResult = result as GetTasksToolResult | null;
    return (getTasksResult?.tasks?.length ?? 0) > 0;
  }

  // Use expandable for getTaskStats when we have detailed stats
  if (toolName === "getTaskStats") {
    const getTaskStatsResult = result as GetTaskStatsResult | null;
    return !!getTaskStatsResult;
  }

  // Use expandable for getNextTask when we have task details
  if (toolName === "getNextTask") {
    const getNextTaskResult = result as GetNextTaskResult | null;
    return !!getNextTaskResult?.task;
  }

  return false;
}

function renderExpandableContent(
  part: Extract<FpMessagePart, { type: "tool-call" }>,
) {
  const { toolName } = part;
  const result = getTaskResult(part);

  if (toolName === "createTasks") {
    const createTasksResult = result as CreateTasksResult | null;
    if (createTasksResult?.tasks) {
      const tasks = Array.isArray(createTasksResult.tasks)
        ? createTasksResult.tasks
        : [];
      return (
        <div className="space-y-1">
          {tasks.map((task, index: number) => (
            <div
              key={task.id || index}
              className="flex items-center gap-2 text-sm"
            >
              <Square className="w-3 h-3 flex-shrink-0 text-muted-foreground" />
              <span className="flex-1">{task.title}</span>
              {task.priority && task.priority !== "medium" && (
                <span className="text-xs px-1.5 py-0.5 bg-muted rounded text-muted-foreground">
                  {task.priority}
                </span>
              )}
            </div>
          ))}
        </div>
      );
    }
  }

  if (toolName === "getTasks") {
    const getTasksResult = result as GetTasksToolResult | null;
    if (getTasksResult?.tasks) {
      const tasks = Array.isArray(getTasksResult.tasks)
        ? getTasksResult.tasks
        : [];
      const tasksByStatus = tasks.reduce(
        (acc: Record<string, typeof tasks>, task) => {
          if (!acc[task.status]) {
            acc[task.status] = [];
          }
          acc[task.status].push(task);
          return acc;
        },
        {},
      );

      return (
        <div className="space-y-2">
          {Object.entries(tasksByStatus).map(([status, statusTasks]) => (
            <div key={status}>
              <div className="text-xs font-medium mb-1 flex items-center gap-1 text-muted-foreground">
                {status === "completed" && (
                  <CircleCheck className="w-3 h-3 text-green-600" />
                )}
                {status === "in_progress" && (
                  <Clock className="w-3 h-3 text-blue-600" />
                )}
                {status === "pending" && (
                  <Square className="w-3 h-3 text-muted-foreground" />
                )}
                {status === "cancelled" && (
                  <AlertCircle className="w-3 h-3 text-orange-600" />
                )}
                <span className="capitalize">
                  {status.replace("_", " ")} ({statusTasks.length})
                </span>
              </div>
              <div className="space-y-0.5 pl-4">
                {statusTasks.map((task) => (
                  <div key={task.id} className="text-sm">
                    {task.title}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      );
    }
  }

  if (toolName === "getTaskStats") {
    const getTaskStatsResult = result as GetTaskStatsResult | null;
    if (getTaskStatsResult) {
      const {
        completed = 0,
        pending = 0,
        inProgress = 0,
        cancelled = 0,
      } = getTaskStatsResult;
      return (
        <div className="grid grid-cols-2 gap-2 text-sm">
          <div className="flex items-center gap-1">
            <CircleCheck className="w-3 h-3 text-green-600" />
            <span>{completed}</span>
          </div>
          <div className="flex items-center gap-1">
            <Clock className="w-3 h-3 text-blue-600" />
            <span>{inProgress}</span>
          </div>
          <div className="flex items-center gap-1">
            <Square className="w-3 h-3 text-muted-foreground" />
            <span>{pending}</span>
          </div>
          {cancelled > 0 && (
            <div className="flex items-center gap-1">
              <AlertCircle className="w-3 h-3 text-orange-600" />
              <span>{cancelled}</span>
            </div>
          )}
        </div>
      );
    }
  }

  if (toolName === "getNextTask") {
    const getNextTaskResult = result as GetNextTaskResult | null;
    if (getNextTaskResult?.task) {
      const task = getNextTaskResult.task;
      return (
        <div className="space-y-1">
          <div className="font-medium text-sm">{task.title}</div>
          {task.description && (
            <div className="text-muted-foreground text-xs">
              {task.description}
            </div>
          )}
          {task.priority && task.priority !== "medium" && (
            <div className="flex items-center gap-2 text-xs">
              <span className="px-1.5 py-0.5 bg-muted rounded text-muted-foreground">
                {task.priority}
              </span>
            </div>
          )}
        </div>
      );
    }
  }

  return null;
}

export function TaskManagement({ part }: TaskManagementProps) {
  const Icon = getTaskIcon(part.toolName);
  const title = getTaskTitle(part);
  const useExpandable = shouldUseExpandableCard(part);

  if (useExpandable) {
    const expandableContent = renderExpandableContent(part);
    return (
      <ExpandableMessageCard
        status={part.status}
        result={part.result}
        icon={Icon}
        title={title}
        accordionValue={`task-${part.toolName}`}
      >
        {expandableContent}
      </ExpandableMessageCard>
    );
  }

  return <SimpleMessageCard status={part.status} icon={Icon} title={title} />;
}
